import React, { useEffect, useState } from 'react';
import { getProducts } from '../dataManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import ProductItemCard from './ProductItemCard';
import useBidirectionalScroll from '../hooks/useBidirectionalScroll';

export default function ProductDetailsPage({ product: initialProduct, navigateTo }) {
  const [product] = useState(initialProduct);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [selectedImage, setSelectedImage] = useState(null);

  // Related products: same categoryId, not the same product
  const allProducts = getProducts();
  const relatedProducts = allProducts.filter(
    p => p.categoryId === (product?.categoryId) && p.id !== product?.id
  );

  useBidirectionalScroll('.product-details-new-container', 100);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [initialProduct]);

  if (!product) {
    return (
      <div className="product-details-empty">
        <h2>No Product Selected</h2>
        <button className="outline-button" onClick={() => navigateTo('products')}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Catalog
        </button>
      </div>
    );
  }

  const handleWhatsApp = () => {
    const text = encodeURIComponent(`Hello, I am interested in ${product.name}. Please provide a quote.`);
    window.open(`https://wa.me/919723962464?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const box = card.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    const centerX = box.width / 2;
    const centerY = box.height / 2;
    const rotateX = (centerY - y) / 15;
    const rotateY = (x - centerX) / 15;

    setTilt({ x: rotateX, y: rotateY });
  };

  const resetTilt = () => {
    setTilt({ x: 0, y: 0 });
  };

  const getProductDescription = (p) => {
    if (p.description && p.description !== 'No description' && !p.description.startsWith('Hand') && !p.description.startsWith('Double') && !p.description.startsWith('Crucible')) return p.description;

    const name = p.name || '';
    
    if (name.includes('Hand Rolling Mill') || name.includes('Mill')) {
      return `The ${name} provides professional-grade metal reduction for fine jewelry manufacturing. Engineered with hardened alloy steel rollers and micro-adjustable thickness controls, it delivers smooth, continuous sheets and perfectly formed wires tailored for exacting artisan workflows.`;
    }
    
    if (name.includes('Polisher') || name.includes('Finishing')) {
      return `Elevate the final luster of your precious metals using the ${name}. Built around advanced low-vibration housing and robust velocity control mechanisms, it removes operational micro-abrasions swiftly while maintaining an environment-conscious enclosed framework.`;
    }
    
    if (name.includes('Press')) {
      return `Equipped with superior multi-stage compression capabilities, the ${name} executes demanding stamping, bending, and forming profiles securely. Optimal hydraulic force guarantees smooth depth calibration across repeated processing cycles safely.`;
    }
    
    if (name.includes('Furnace') || name.includes('Melting')) {
      return `Providing high-efficiency thermal output targets, the ${name} quickly reaches casting benchmarks reliably. Featuring intelligent automated safety shutoffs and excellent heat insulation containment schemas uniformly.`;
    }
    
    if (name.includes('Lathe') || name.includes('Expanding') || name.includes('Machine')) {
      return `The ${name} serves as a rugged workstation essential for multi-material dimension sculpting reliably. Highly optimized component ergonomics support micro-precision scaling reliably over extensive deployment lifespans safely.`;
    }

    if (name.includes('Crucible')) {
      return `Specifically optimized for extreme high-temperature melting phases, the ${name} protects critical materials safely. Anti-stick graphite barriers assure minimal slag buildup allowing optimized operational continuity.`;
    }

    return `The ${name} offers top-tier mechanical deployment specs engineered exclusively for heavy manufacturing utility. Micro-tolerance stability ensures absolute consistency under high duty cycle operation safely.`;
  };

  const getNormalizedSpecs = (p) => {
    if (!p) return [];
    
    // 1. If we have technicalSpecs as an array (live database format)
    if (Array.isArray(p.technicalSpecs) && p.technicalSpecs.length > 0) {
      return p.technicalSpecs.filter(
        spec => spec && spec.key && !['Category', 'Note'].includes(spec.key)
      );
    }
    
    // 2. If we have specs as an object (static legacy format)
    if (p.specs && typeof p.specs === 'object') {
      return Object.entries(p.specs)
        .filter(([key]) => !['Category', 'Note'].includes(key))
        .map(([key, value]) => ({ key, value }));
    }
    
    return [];
  };

  const getSpecificFeatures = (p) => {
    if (Array.isArray(p.keySpecs) && p.keySpecs.length > 0) {
      return p.keySpecs;
    }
    // Fallback generic bullets (technical specs will only render in the full specs table below)
    return [
      `Strict engineering tolerance benchmarks for ${p.name}`,
      `Customized industrial profile tailored to SKU`,
      `Durable anti-corrosive coating finishes for high endurance`,
      `Highly modular integration ready right out of the box`
    ];
  };

  const imageUrl = product.images?.[0]?.url || product.img;

  return (
    <div className="product-details-new-container">

      <nav className="product-details-nav">
        <button className="back-btn-minimal" onClick={() => navigateTo('products')}>
          <FontAwesomeIcon icon={faArrowLeft} /> {product.category?.name || 'Products'} / {product.name}
        </button>
      </nav>

      <div className="product-details-main-grid">

        <div className="product-visual-column">
          <div
            className="product-image-card"
            onMouseMove={handleMouseMove}
            onMouseLeave={resetTilt}
            style={{
              transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <img 
              src={imageUrl} 
              alt={product.name} 
              className="main-product-img" 
              onError={(e) => { e.target.src = 'https://via.placeholder.com/600?text=Image+Not+Found'; }}
            />
            <div className="image-overlay-glow"></div>
          </div>
        </div>

        <div className="product-info-column">
          <div className="product-header-block">
            <span className="industrial-badge">N.D. Tools & Machinery</span>
            <h1 className="product-main-title">{product.name}</h1>
          </div>

          <div className="product-description-container">
            <p className="product-primary-desc">
              {getProductDescription(product)}
            </p>
          </div>

          <div className="product-features-block">
            <h4 className="block-label">Key Specifications</h4>
            <ul className="caps-list">
              {getSpecificFeatures(product).map((feature, idx) => (
                <li key={idx}>
                  <FontAwesomeIcon icon={faCheckCircle} className="check-icon" /> {feature}
                </li>
              ))}
            </ul>
          </div>

          <div className="product-cta-block">
            <button className="whatsapp-inquiry-btn" onClick={handleWhatsApp}>
              <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Inquiry
            </button>
          </div>
        </div>

      </div>

      {/* ── Specs Section ── */}
      {(product.specsTable || getNormalizedSpecs(product).length > 0) && (
        <section className="technical-specs-section">
          <div className="section-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="industrial-badge" style={{ marginBottom: '1rem' }}>Technical Data</span>
            <h2 className="specs-main-title">Full Technical Specifications</h2>
          </div>

          {/* If specsTable exists → show ONLY the comparison table */}
          {product.specsTable ? (
            <div className="specs-table-wrapper">
              <table className="specs-table">
                <thead>
                  <tr>
                    {product.specsTable.headers.map((h, i) => (
                      <th key={i}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {product.specsTable.rows.map((row, rIdx) => (
                    <tr key={rIdx}>
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className={cIdx === 0 ? 'spec-label' : 'spec-value'}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* No specsTable → show single key-value table */
            <div className="specs-table-wrapper">
              <table className="specs-table">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {getNormalizedSpecs(product).map((spec, idx) => (
                    <tr key={idx}>
                      <td className="spec-label">{spec.key}</td>
                      <td className="spec-value">{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {relatedProducts.length > 0 && (
        <section className="related-products-section" style={{ marginTop: '8rem', borderTop: '1px solid var(--border-color)', paddingTop: '6rem' }}>
          <div className="section-header" style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <span className="industrial-badge" style={{ marginBottom: '1rem' }}>Discovery Selection</span>
            <h2 className="specs-main-title">Related <span style={{ color: 'var(--accent-color)' }}>Machinery</span></h2>
            <p style={{ color: 'var(--text-muted)', maxWidth: '600px', margin: '1rem auto' }}>
              Explore other high-performance solutions within the same industrial category.
            </p>
          </div>

          <div className="products-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '3rem',
            maxWidth: '1440px',
            margin: '0 auto'
          }}>
            {relatedProducts.map((p, idx) => (
              <ProductItemCard
                key={p.id}
                product={p}
                idx={idx}
                navigateTo={navigateTo}
                setSelectedImage={setSelectedImage}
              />
            ))}
          </div>
        </section>
      )}

      {selectedImage && (
        <div className="lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <span className="lightbox-close" onClick={() => setSelectedImage(null)}>&times;</span>
          <img src={selectedImage} alt="Full Screen Item" className="lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

    </div>
  );
}